import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Save, Link2, Landmark, DollarSign } from "lucide-react";

export default function Settings() {
  const [link, setLink] = useState("");
  const [instructions, setInstructions] = useState("");
  const [institutions, setInstitutions] = useState("");
  const [serviceFee, setServiceFee] = useState("50");
  const [mailFee, setMailFee] = useState("0");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let ignore = false;
    api.get("/settings").then((r) => {
      if (ignore) return;
      setLink(r.data.idiq_affiliate_link || "");
      setInstructions(r.data.private_banking_instructions || "");
      setInstitutions((r.data.private_banking_institutions || []).join("\n"));
      setServiceFee(String(r.data.service_fee ?? 50));
      setMailFee(String(r.data.certified_letter_price ?? 11.02));
      setLoaded(true);
    }).catch(() => {});
    return () => { ignore = true; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        idiq_affiliate_link: link,
        private_banking_instructions: instructions,
        private_banking_institutions: institutions.split("\n").map((s) => s.trim()).filter(Boolean),
        service_fee: Math.max(0, parseFloat(serviceFee) || 0),
        certified_letter_price: Math.max(0, parseFloat(mailFee) || 0),
      });
      toast.success("Settings saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); } finally { setSaving(false); }
  };

  return (
    <div className="animate-fade-up max-w-2xl">
      <div className="mb-8">
        <p className="text-xs tracking-[0.2em] uppercase text-primary font-bold mb-2">Configuration</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-none">Settings</h1>
      </div>

      <Card className="p-6 bg-card/60 border-border mb-6" data-testid="pricing-settings-card">
        <h3 className="font-display text-xl font-semibold mb-1 flex items-center gap-2"><DollarSign className="h-5 w-5 text-primary" /> Letter Printing Fees</h3>
        <p className="text-sm text-muted-foreground mb-4">The client pays your service fee + the USPS Certified Mail price (with Return Receipt) for all 3 bureau letters. Postage is the certified letter price below for each of the 3 letters (+$0.29 per extra ounce on heavy letters). You can waive or change your fee for any client when you request payment. Once paid, you can print the 3 bureau letters for that round and mail them yourself.</p>
        <div className="grid sm:grid-cols-3 gap-3 items-end">
          <div className="space-y-2"><Label>Your Service Fee ($)</Label><Input type="number" min="0" step="0.01" value={serviceFee} disabled={!loaded} onChange={(e) => setServiceFee(e.target.value)} data-testid="service-fee-input" /></div>
          <div className="space-y-2"><Label>Certified Letter Price ($ each)</Label><Input type="number" min="0" step="0.01" value={mailFee} disabled={!loaded} onChange={(e) => setMailFee(e.target.value)} data-testid="letter-price-input" /><div className="text-[11px] text-muted-foreground">USPS today: $0.82 + $5.55 Certified + $4.65 Return Receipt = $11.02</div></div>
          <div className="text-sm pb-6" data-testid="fee-total">Client pays about <span className="font-mono font-semibold text-primary">${((parseFloat(serviceFee) || 0) + 3 * (parseFloat(mailFee) || 0)).toFixed(2)}</span></div>
        </div>
      </Card>

      <Card className="p-6 bg-card/60 border-border" data-testid="idiq-settings-card">
        <h3 className="font-display text-xl font-semibold mb-1 flex items-center gap-2"><Link2 className="h-5 w-5 text-primary" /> IdentityIQ Affiliate Link</h3>
        <p className="text-sm text-muted-foreground mb-4">Paste your IDIQ affiliate/referral link. Clients enroll through this link (used by the "Enroll on IdentityIQ" button on each client) so your referrals and commissions are tracked. Apply at idiq.com/partnerships/affiliate-partners.</p>
        <div className="space-y-2">
          <Label>Affiliate Link</Label>
          <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://www.identityiq.com/....?offercode=YOUR_CODE" data-testid="affiliate-link-input" />
        </div>
      </Card>

      <Card className="p-6 bg-card/60 border-border mt-6" data-testid="pb-settings-card">
        <h3 className="font-display text-xl font-semibold mb-1 flex items-center gap-2"><Landmark className="h-5 w-5 text-primary" /> Private Banking</h3>
        <p className="text-sm text-muted-foreground mb-4">These appear on the Private Banking page and on each client (gated at 580+). Edit the approval instructions and the partner institutions clients can apply to.</p>
        <div className="space-y-2">
          <Label>Approval Instructions</Label>
          <Textarea rows={9} value={instructions} onChange={(e) => setInstructions(e.target.value)} data-testid="pb-instructions-input" />
        </div>
        <div className="space-y-2 mt-4">
          <Label>Partner Institutions (one per line)</Label>
          <Textarea rows={6} value={institutions} onChange={(e) => setInstitutions(e.target.value)} placeholder={"JPMorgan Private Bank\nBank of America Private Bank"} data-testid="pb-institutions-input" />
        </div>
      </Card>

      <Button onClick={save} disabled={saving || !loaded} className="rounded-lg mt-6" data-testid="save-settings-btn">{saving ? "Saving…" : <><Save className="h-4 w-4 mr-1" /> Save Settings</>}</Button>
    </div>
  );
}
